/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useCrmDeals.ts
 * PURPOSE: CRM pipeline deals from dashboard /api/v1/crm/deals — optimistic patch with rollback
 * DEPENDENCIES: react, @shared/lib/frontend/api/crmApi, ./crmEntityMappers, ../types/opportunity.types
 * PUBLIC_API: useCrmDeals, CRM_NEW_OPPORTUNITY_SENTINEL
 * SIDE_EFFECTS: HTTP
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 362/400
 * RECENT: 2026-07-08 [2.2.1552] — Background refetch after first load (no KPI skeleton flash on realtime poll).
 * RECENT_PRIOR: 2026-05-11 — Sentinel **`crmCreate`** gated on name (not **`—`**), **`accountId`**, **`closeDateISO`**, and **`amount >= 0`** (GitHub #84)
 * RECENT_PRIOR: 2026-05-11 Task E — blank-name saveOpportunity sentinel calls patchOpportunity(SENTINEL, dataPatch) then return; useOpportunity forwards draft name on savePartial and saveAll when patch omits name
 * RECENT_PRIOR: 2026-05-11 [2.2.942] CRM finalisation Tasks 1+2 — new-deal sentinel hardening: **`saveOpportunity`** short-circuits server **`crmCreate`** when **`dataPatch.name`** is blank/whitespace; **`patchOpportunity(SENTINEL, …)`** list branch is a no-op; **`refetch`** **`setData(mapped)`** — root **`CHANGELOG` [2.2.942]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.876] CRM persistence UX compliance: persisted CRM create, edit, task, activity, deal, account, related-entity, and pipeline run-history flows; stale demo copy removed — root **`CHANGELOG` [2.2.876]`**
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { crmCreate, crmDelete, crmList, crmPatch } from '@shared/lib/frontend/api/crmApi';
import { invalidateGoldenCrmQueries } from '@shared/lib/frontend/api/goldenCrmQuerySync';
import { OPPORTUNITY_DEAL_FRAMEWORK_PATCH_KEYS } from '../data/opportunityDealFrameworkSections';
import type { Opportunity } from '../types/opportunity.types';
import { mapSalesOpportunityRowToCrm } from './crmEntityMappers';

export const CRM_NEW_OPPORTUNITY_SENTINEL = '__crm_new__';

function applyDerivedOpportunity(next: Opportunity): Opportunity {
  const amount = Number(next.amount);
  const probability = Number(next.probability);
  if (Number.isFinite(amount) && Number.isFinite(probability)) {
    return { ...next, weightedAmount: Math.round((amount * probability) / 100) };
  }
  return next;
}

/** Only include fields present on `changed` so PATCH does not resend e.g. closeDateISO on every amount edit (avoids DATE bind issues on the server). */
function dealToPatchBody(merged: Opportunity, changed: Partial<Opportunity>): Record<string, unknown> {
  const keys = new Set(Object.keys(changed).filter((k) => k !== 'id'));
  const pick = (k: keyof Opportunity) => keys.has(k as string);
  const out: Record<string, unknown> = {};
  if (pick('name') && merged.name !== undefined) out.name = merged.name;
  if (pick('stage') && merged.stage !== undefined) out.stage = merged.stage;
  if (pick('amount') && merged.amount !== undefined) out.amount = merged.amount;
  if (pick('probability') && merged.probability !== undefined) out.probability = merged.probability;
  if (
    pick('closeDateISO') &&
    merged.closeDateISO !== undefined &&
    merged.closeDateISO.length >= 10
  ) {
    out.close_date = merged.closeDateISO.slice(0, 10);
  }
  if (pick('accountId')) {
    const aid = merged.accountId;
    // Empty string (not JSON null) so DealPatch sees a set field; router uses `is not None` before UPDATE.
    out.account_id = aid != null && String(aid).trim() !== '' ? String(aid).trim() : '';
  }
  if (pick('primaryContactId')) {
    const v = merged.primaryContactId;
    out.primary_contact_id =
      v != null && String(v).trim() !== '' ? String(v).trim() : '';
  }
  if (pick('ownerId') && merged.ownerId !== undefined) out.ownerId = merged.ownerId;
  if (pick('backupOwnerId')) out.backupOwnerId = merged.backupOwnerId ?? '';
  if (pick('source') && merged.source !== undefined) out.source = merged.source;
  if (pick('territoryId')) out.territory_id = merged.territoryId;
  if (pick('nextStep')) out.next_step = merged.nextStep;
  if (pick('notes')) out.notes = merged.notes;
  if (pick('lostReason')) out.lost_reason = merged.lostReason ?? null;
  if (pick('tags') && merged.tags !== undefined) out.tags = merged.tags;
  for (const k of OPPORTUNITY_DEAL_FRAMEWORK_PATCH_KEYS) {
    if (pick(k)) {
      const v = merged[k];
      (out as Record<string, unknown>)[k as string] = v === undefined || v === '' ? null : v;
    }
  }
  return out;
}

export type DealDeleteCommitHandlers = {
  onCommitted: () => void;
  onCommitFailed: (err: unknown) => void;
};

export type UseCrmDealsMutate = {
  replaceRemote: (rows: Opportunity[]) => void;
  patchOpportunity: (id: string, patch: Partial<Opportunity>) => void;
  addOpportunity: (opportunity: Opportunity) => void;
  persistOpportunityPatch: (patch: Partial<Opportunity>) => Promise<void>;
  /** PATCH `{ stage }` with the same optimistic list update and rollback as `persistOpportunityPatch`. */
  patchDealStage: (id: string, stage: Opportunity['stage']) => Promise<void>;
  saveOpportunity: (data: Partial<Opportunity>) => Promise<Opportunity | undefined>;
  /** Immediate DELETE + refetch (bulk delete, legacy callers). */
  deleteRemote: (id: string) => Promise<void>;
  /**
   * After the delay, DELETEs the deal on the server and refetches. Call `cancel()` to abort (Undo).
   * Caller owns optimistic UI (e.g. `removeOpportunity`) before scheduling.
   */
  scheduleDealDeleteCommit: (id: string, handlers: DealDeleteCommitHandlers) => () => void;
};

export type UseCrmDealsResult = {
  data: Opportunity[];
  isLoading: boolean;
  error: string | null;
  mutate: UseCrmDealsMutate;
  refetch: () => Promise<void>;
};

const DEAL_DELETE_UNDO_MS = 5000;

export function useCrmDeals(): UseCrmDealsResult {
  const queryClient = useQueryClient();
  const [data, setData] = useState<Opportunity[]>([]);
  const dataRef = useRef<Opportunity[]>([]);
  dataRef.current = data;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** After the first fetch, background refetches skip the loading skeleton (realtime / poll). */
  const hasLoadedOnceRef = useRef(false);
  /** Browser timer ids — `window.setTimeout` returns a number under DOM typings (Node `Timeout` differs). */
  const pendingDealDeleteTimers = useRef(new Map<string, number>());

  const refetch = useCallback(async () => {
    const showLoading = !hasLoadedOnceRef.current;
    if (showLoading) setIsLoading(true);
    setError(null);
    try {
      const res = await crmList<Record<string, unknown>>('deals', { limit: 200 });
      const rows = Array.isArray(res.items) ? res.items : [];
      const mapped = rows.map((r) => mapSalesOpportunityRowToCrm(r));
      // The sentinel draft is owned by `useOpportunity` local state, never
      // injected into this list (see patchOpportunity sentinel branch), so
      // refetch can replace `prev` outright with the mapped server rows.
      setData(mapped);
      invalidateGoldenCrmQueries(queryClient);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      hasLoadedOnceRef.current = true;
      setIsLoading(false);
    }
  }, [queryClient]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const patchOpportunity = useCallback((id: string, patch: Partial<Opportunity>) => {
    setData((prev) => {
      const now = new Date().toISOString();
      const idx = prev.findIndex((o) => o.id === id);
      if (idx === -1 && id === CRM_NEW_OPPORTUNITY_SENTINEL) {
        // Draft is managed by useOpportunity local state only; never inject the
        // sentinel into the shared crmOpportunities list (would cause it to
        // surface in pipelines/lists/refetch merges before a server id exists).
        return prev;
      }
      if (idx === -1) return prev;
      const merged = applyDerivedOpportunity({
        ...prev[idx],
        ...patch,
        id: prev[idx].id,
        updatedAtISO: now,
      });
      return prev.map((o, i) => (i === idx ? merged : o));
    });
  }, []);

  const addOpportunity = useCallback((opportunity: Opportunity) => {
    const now = new Date().toISOString();
    setData((prev) => {
      if (prev.some((o) => o.id === opportunity.id)) {
        return prev.map((o) =>
          o.id === opportunity.id ? applyDerivedOpportunity({ ...o, ...opportunity, updatedAtISO: now }) : o
        );
      }
      return [
        ...prev,
        applyDerivedOpportunity({
          ...opportunity,
          createdAtISO: opportunity.createdAtISO ?? now,
          updatedAtISO: now,
        }),
      ];
    });
  }, []);

  const replaceRemote = useCallback((rows: Opportunity[]) => {
    setData(rows);
  }, []);

  const replaceOneFromResponse = useCallback((id: string, row: Record<string, unknown>) => {
    if (String(row.id ?? '') !== id) return;
    const mapped = mapSalesOpportunityRowToCrm(row);
    setData((rows) => rows.map((o) => (o.id === id ? mapped : o)));
  }, []);

  const persistOpportunityPatch = useCallback(async (patch: Partial<Opportunity>) => {
    const id = patch.id;
    if (!id || id === CRM_NEW_OPPORTUNITY_SENTINEL) return;
    const prevSnap = structuredClone(dataRef.current);
    const target = dataRef.current.find((o) => o.id === id);
    if (!target) return;
    patchOpportunity(id, patch);
    try {
      const merged = { ...target, ...patch };
      const updated = await crmPatch<Record<string, unknown>>(
        'deals',
        id,
        dealToPatchBody(applyDerivedOpportunity(merged), patch)
      );
      replaceOneFromResponse(id, updated);
      invalidateGoldenCrmQueries(queryClient);
    } catch (e) {
      replaceRemote(prevSnap);
      throw e;
    }
  }, [patchOpportunity, replaceOneFromResponse, replaceRemote, queryClient]);

  const patchDealStage = useCallback(
    async (id: string, stage: Opportunity['stage']) => {
      await persistOpportunityPatch({ id, stage });
    },
    [persistOpportunityPatch]
  );

  const saveOpportunity = useCallback(
    async (dataPatch: Partial<Opportunity>): Promise<Opportunity | undefined> => {
      const prevSnap = structuredClone(dataRef.current);
      const targetId = dataPatch.id ?? '';
      if (!targetId) {
        throw new Error('saveOpportunity requires id on the patch (sentinel or persisted deal id).');
      }
      applyDerivedFieldsToPatch(dataPatch);
      if (targetId === CRM_NEW_OPPORTUNITY_SENTINEL) {
        // Gate server create on a non-blank name so partial field edits (stage,
        // owner, amount, etc.) on the inline "new deal" row don't persist a
        // half-formed row server-side before the user has named the deal.
        // No optimistic `patchOpportunity` call here: the sentinel draft is
        // owned by `useOpportunity` local state, and `patchOpportunity` is a
        // no-op for the sentinel id by design — see its sentinel branch.
        const dealName = (dataPatch.name ?? '').trim();
        if (!dealName || dealName === '—') {
          patchOpportunity(CRM_NEW_OPPORTUNITY_SENTINEL, dataPatch);
          return undefined;
        }
        const accountId = (dataPatch.accountId ?? '').toString().trim();
        if (!accountId) {
          patchOpportunity(CRM_NEW_OPPORTUNITY_SENTINEL, dataPatch);
          return undefined;
        }
        const closeIso = dataPatch.closeDateISO;
        if (!closeIso || String(closeIso).trim().length < 10) {
          patchOpportunity(CRM_NEW_OPPORTUNITY_SENTINEL, dataPatch);
          return undefined;
        }
        const amt = Number(dataPatch.amount);
        if (!Number.isFinite(amt) || amt < 0) {
          patchOpportunity(CRM_NEW_OPPORTUNITY_SENTINEL, dataPatch);
          return undefined;
        }
        try {
          const created = await crmCreate<Record<string, unknown>>('deals', {
            name: dealName,
            stage: String(dataPatch.stage ?? 'prospecting'),
            amount: amt,
            probability: Number(dataPatch.probability ?? 10),
            close_date: String(closeIso).trim().slice(0, 10),
            account_id: accountId,
            owner_id: dataPatch.ownerId,
            primary_contact_id: dataPatch.primaryContactId,
            source: dataPatch.source,
            territory_id: dataPatch.territoryId,
            next_step: dataPatch.nextStep,
            notes: dataPatch.notes,
            tags: dataPatch.tags,
          });
          const mapped = mapSalesOpportunityRowToCrm(created);
          setData((rows) => rows.filter((o) => o.id !== CRM_NEW_OPPORTUNITY_SENTINEL).concat(mapped));
          invalidateGoldenCrmQueries(queryClient);
          return mapped;
        } catch (e) {
          replaceRemote(prevSnap);
          throw e;
        }
      }
      const cur = dataRef.current.find((o) => o.id === targetId);
      patchOpportunity(targetId, dataPatch);
      try {
        const updated = await crmPatch<Record<string, unknown>>(
          'deals',
          targetId,
          dealToPatchBody(
            applyDerivedOpportunity({ ...(cur ?? {}), ...dataPatch } as Opportunity),
            dataPatch
          )
        );
        replaceOneFromResponse(targetId, updated);
        invalidateGoldenCrmQueries(queryClient);
        return mapSalesOpportunityRowToCrm(updated);
      } catch (e) {
        replaceRemote(prevSnap);
        throw e;
      }
    },
    [patchOpportunity, replaceOneFromResponse, replaceRemote, queryClient]
  );

  const deleteRemote = useCallback(
    async (id: string) => {
      await crmDelete('deals', id);
      await refetch();
    },
    [refetch]
  );

  const scheduleDealDeleteCommit = useCallback(
    (id: string, handlers: DealDeleteCommitHandlers) => {
      const prev = pendingDealDeleteTimers.current.get(id);
      if (prev != null) clearTimeout(prev);
      const tid = window.setTimeout(() => {
        pendingDealDeleteTimers.current.delete(id);
        void (async () => {
          try {
            await crmDelete('deals', id);
            await refetch();
            handlers.onCommitted();
          } catch (e) {
            handlers.onCommitFailed(e);
          }
        })();
      }, DEAL_DELETE_UNDO_MS);
      pendingDealDeleteTimers.current.set(id, tid);
      return () => {
        const t = pendingDealDeleteTimers.current.get(id);
        if (t != null) clearTimeout(t);
        pendingDealDeleteTimers.current.delete(id);
      };
    },
    [refetch]
  );

  const mutate = useMemo(
    () =>
      ({
        replaceRemote,
        patchOpportunity,
        addOpportunity,
        persistOpportunityPatch,
        patchDealStage,
        saveOpportunity,
        deleteRemote,
        scheduleDealDeleteCommit,
      }) satisfies UseCrmDealsMutate,
    [
      replaceRemote,
      patchOpportunity,
      addOpportunity,
      persistOpportunityPatch,
      patchDealStage,
      saveOpportunity,
      deleteRemote,
      scheduleDealDeleteCommit,
    ]
  );

  return { data, isLoading, error, mutate, refetch };
}

function applyDerivedFieldsToPatch(p: Partial<Opportunity>): void {
  const amount = Number(p.amount);
  const probability = Number(p.probability);
  if (Number.isFinite(amount) && Number.isFinite(probability)) {
    (p as { weightedAmount?: number }).weightedAmount = Math.round((amount * probability) / 100);
  }
}
